import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { Button, Modal } from "antd";

import { DeleteProfileModal } from "../components/profile/DeleteProfileModal";
import { EmailConfirmationModal } from "../components/profile/EmailConfirmationModal";
import { GreetingModal } from "../components/profile/GreetingModal";
import { SubscriptionConformanceModal } from "../components/subscription/SubscriptionConformanceModal";
import {
  type DialogMode,
  SubscriptionDialog,
} from "../components/subscription/SubscriptionDialog";
import { SubscriptionRestoreModal } from "../components/subscription/SubscriptionRestoreModal";
import { SupportSuccessModal } from "../components/support/SupportSuccessModal";
import { TeamAddMemberModal } from "../components/teammanage/TeamAddMemberModal";
import { TeamSizeAlertModal } from "../components/teammanage/TeamSizeAlertModal";
import { WebGisCreatingModal } from "../components/webgis/WebGisCreatingModal";
import { WebGisSuccessModal } from "../components/webgis/WebGisSuccessModal";

const ALLOWED_HOSTS = ["localhost", "127.0.0.1", "my.nextgis.dev"];
interface ConformanceScenario {
  label: string;
  violations: string[];
}

const CONFORMANCE_SCENARIOS: ConformanceScenario[] = [
  {
    label: "Users (1 allowed)",
    violations: ["user_limit"],
  },
  {
    label: "Storage (10 GB allowed)",
    violations: ["storage_limit"],
  },
  {
    label: "Collectors",
    violations: ["resource_limit"],
  },
  {
    label: "Trackers",
    violations: ["tracker"],
  },
  {
    label: "Private resources",
    violations: ["permissions"],
  },
  {
    label: "unknown_violation",
    violations: ["alien_portal_connection"],
  },
  {
    label: "All violations",
    violations: [
      "user_limit",
      "resource_limit",
      "tracker",
      "storage_limit",
      "permissions",
    ],
  },
];

export const TestModalsPage = () => {
  const { t } = useTranslation();

  // Profile modals
  const [greetingOpen, setGreetingOpen] = useState(false);
  const [deleteProfileOpen, setDeleteProfileOpen] = useState(false);
  const [deleteProfileSuccessOpen, setDeleteProfileSuccessOpen] =
    useState(false);
  const [emailConfirmSendOpen, setEmailConfirmSendOpen] = useState(false);
  const [emailConfirmVerifyOpen, setEmailConfirmVerifyOpen] = useState(false);

  // Subscription dialog (all modes share one state)
  const [subDialogMode, setSubDialogMode] = useState<DialogMode>(null);

  // Subscription restore dialog
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);

  // Subscription conformance dialog
  const [conformanceScenario, setConformanceScenario] =
    useState<ConformanceScenario | null>(null);

  // WebGIS modals
  const [webgisSuccessOpen, setWebgisSuccessOpen] = useState(false);
  const [webgisLoadingOpen, setWebgisLoadingOpen] = useState(false);

  // Support success modal
  const [supportSuccessOpen, setSupportSuccessOpen] = useState(false);

  // Team modals
  const [teamAddOpen, setTeamAddOpen] = useState(false);
  const [teamSizeAlertOpen, setTeamSizeAlertOpen] = useState(false);

  if (!ALLOWED_HOSTS.includes(window.location.hostname)) {
    return <Navigate to="/" replace />;
  }

  const subDialogModes: { mode: NonNullable<DialogMode>; label: string }[] = [
    { mode: "created", label: "created" },
    { mode: "creation_failed", label: "creation_failed" },
    { mode: "updated", label: "updated" },
    { mode: "update_failed", label: "update_failed" },
    { mode: "cancel", label: "cancel" },
    { mode: "restored", label: "restored" },
    { mode: "invoice_requested", label: "invoice_requested" },
  ];

  return (
    <div className="page" style={{ maxWidth: 720 }}>
      <h1 style={{ marginBottom: 4 }}>Modal Test Page</h1>
      <p style={{ color: "rgba(0,0,0,0.45)", marginBottom: 32 }}>
        Only visible on localhost and my.nextgis.dev
      </p>

      {/* ── Profile ── */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ marginBottom: 12 }}>Profile</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Button onClick={() => setGreetingOpen(true)}>Greeting</Button>
          <Button onClick={() => setDeleteProfileOpen(true)}>
            Delete Profile
          </Button>
          <Button onClick={() => setDeleteProfileSuccessOpen(true)}>
            Delete Profile (success)
          </Button>
          <Button onClick={() => setEmailConfirmSendOpen(true)}>
            Email Confirmation (send step)
          </Button>
          <Button onClick={() => setEmailConfirmVerifyOpen(true)}>
            Email Confirmation (verify step)
          </Button>
        </div>
      </section>

      {/* ── Subscription ── */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ marginBottom: 12 }}>Subscription Dialog</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {subDialogModes.map(({ mode, label }) => (
            <Button key={mode} onClick={() => setSubDialogMode(mode)}>
              {label}
            </Button>
          ))}
        </div>
      </section>

      <section style={{ marginBottom: 32 }}>
        <h2 style={{ marginBottom: 12 }}>Subscription Restore</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Button onClick={() => setRestoreDialogOpen(true)}>
            Restore Dialog
          </Button>
        </div>
      </section>

      <section style={{ marginBottom: 32 }}>
        <h2 style={{ marginBottom: 12 }}>Subscription Conformance</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {CONFORMANCE_SCENARIOS.map((scenario) => (
            <Button
              key={scenario.label}
              onClick={() => setConformanceScenario(scenario)}
            >
              {scenario.label}
            </Button>
          ))}
        </div>
      </section>

      {/* ── WebGIS ── */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ marginBottom: 12 }}>WebGIS</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Button onClick={() => setWebgisSuccessOpen(true)}>
            WebGIS Ready
          </Button>
          <Button onClick={() => setWebgisLoadingOpen(true)}>
            WebGIS Creating (loading)
          </Button>
          {webgisLoadingOpen && (
            <Button danger onClick={() => setWebgisLoadingOpen(false)}>
              Force-close loading modal
            </Button>
          )}
        </div>
      </section>

      {/* ── Support ── */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ marginBottom: 12 }}>Support</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Button onClick={() => setSupportSuccessOpen(true)}>
            Request Sent
          </Button>
        </div>
      </section>

      {/* ── Team ── */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ marginBottom: 12 }}>Team</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Button onClick={() => setTeamAddOpen(true)}>Add Member</Button>
          <Button onClick={() => setTeamSizeAlertOpen(true)}>
            Team Size Alert
          </Button>
          <Button
            onClick={() =>
              Modal.confirm({
                title: t("team.deleteUserConfirm", {
                  email: "user@example.com",
                }),
                okText: t("team.remove"),
                cancelText: t("team.cancel"),
              })
            }
          >
            Delete Member (confirm)
          </Button>
        </div>
      </section>

      {/* ═══ Modals ═══ */}

      <GreetingModal
        open={greetingOpen}
        onClose={() => setGreetingOpen(false)}
      />

      <DeleteProfileModal
        open={deleteProfileOpen}
        onClose={() => setDeleteProfileOpen(false)}
      />

      <DeleteProfileModal
        open={deleteProfileSuccessOpen}
        onClose={() => setDeleteProfileSuccessOpen(false)}
        initialSuccess
      />

      <SubscriptionDialog
        mode={subDialogMode}
        expireAt="2026-09-30"
        onClose={() => setSubDialogMode(null)}
      />

      <SubscriptionRestoreModal
        open={restoreDialogOpen}
        onClose={() => setRestoreDialogOpen(false)}
        onConfirm={() => setRestoreDialogOpen(false)}
        loading={false}
        price="€9.99"
        expireAt="2025-12-31"
      />

      <SubscriptionConformanceModal
        open={conformanceScenario !== null}
        planName="Mini"
        violations={conformanceScenario?.violations ?? []}
        onClose={() => setConformanceScenario(null)}
      />

      <WebGisSuccessModal
        open={webgisSuccessOpen}
        onClose={() => setWebgisSuccessOpen(false)}
        webgisUrl="#"
        plan="mandarin"
      />

      <WebGisCreatingModal open={webgisLoadingOpen} />

      <SupportSuccessModal
        open={supportSuccessOpen}
        onClose={() => setSupportSuccessOpen(false)}
        email="test@example.com"
      />

      <TeamAddMemberModal
        open={teamAddOpen}
        onClose={() => setTeamAddOpen(false)}
        onInviteByEmail={async () => {}}
      />

      <TeamSizeAlertModal
        open={teamSizeAlertOpen}
        onClose={() => setTeamSizeAlertOpen(false)}
      />

      {emailConfirmSendOpen && <EmailConfirmationModal initialStep="send" />}
      {emailConfirmVerifyOpen && (
        <EmailConfirmationModal initialStep="verify" />
      )}

      {(emailConfirmSendOpen || emailConfirmVerifyOpen) && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1100,
          }}
        >
          <Button
            danger
            onClick={() => {
              setEmailConfirmSendOpen(false);
              setEmailConfirmVerifyOpen(false);
            }}
          >
            Force-close email confirm modal
          </Button>
        </div>
      )}
    </div>
  );
};
